import React, { ReactNode, Suspense } from 'react';
import { Routes, Route, Navigate, createBrowserRouter } from "react-router-dom";
import Roi from '../modules/middle_off/pages/roi';
import { loggerSetting } from '../utils/util';

// * 中文配置
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import WarningPage from '../modules/middle_off/pages/warning';
import BudgetPage from '../modules/middle_off/pages/budget';
dayjs.locale('zh-cn');

const Loading = React.lazy(() => import('../components/loading'));
const Auth = React.lazy(() => import('../components/auth'));
const Login = React.lazy(() => import('../pages/login'));
const Index = React.lazy(() => import('../pages/index'));
const Layout = React.lazy(() => import('../pages/layout'));
const Home = React.lazy(() => import('../pages/home'));
const GoogleAd = React.lazy(() => import('../pages/google/adsense'));
const GoogleAccountCreate = React.lazy(() => import('../pages/google/account-create'));
const GoogleAccountManagement = React.lazy(() => import('../pages/google/account-management'));
const GoogleManagerLink = React.lazy(() => import('../pages/google/manager-link'));
const FacebookAd = React.lazy(() => import('../pages/facebook/adsense'));
const DemandAd = React.lazy(() => import('../pages/demand/adsense'));
const DemandCreate = React.lazy(() => import('../pages/demand/create'));
const DemandRoi = React.lazy(() => import('../pages/demand/roi'));
const DisplayAd = React.lazy(() => import('../pages/display'));
const GoogleAdjustment = React.lazy(() => import('../pages/google/adjustment'));
const GooglePausedCampaigns = React.lazy(() => import('../pages/google/paused-campaigns'));
const FacebookAdjustment = React.lazy(() => import('../pages/facebook/adjustment'));
const SnapAdjustment = React.lazy(() => import('../pages/snap/adjustment'));
const TikTokAdjustment = React.lazy(() => import('../pages/tiktok/adjustment'));
const LandingPageList = React.lazy(() => import('../pages/landing'));
const PeriodicDataList = React.lazy(() => import('../pages/periodic-data'));
const RefusedAds = React.lazy(() => import('@/pages/refusedads'));
const Privacy = React.lazy(() => import('../pages/privacy'));
const Terms = React.lazy(() => import('../pages/terms'));
const CopyWriting = React.lazy(() => import('../pages/copywriting'));
const CountryStore = React.lazy(() => import('../pages/countrystore'));
const GmailHourlyMetrics = React.lazy(() => import('../pages/gmail-hourly-metrics'));
const AdManagerDeviceSite = React.lazy(() => import('../pages/admanager-device-site'));
const AdManagerSiteHourly = React.lazy(() => import('../pages/admanager-site-hourly'));

const RedirectHome = <Navigate to='/home' replace />;

loggerSetting('info');


const lazyload= (children: ReactNode): ReactNode => {

    return (<Suspense fallback={<Loading/>}>{children}</Suspense>);
}

const routes = [
    {
        name: 'layout',
        element: lazyload(<Auth><Layout/></Auth>),
        children: [
            {
            name: 'home',
            path:'/home',
            element:lazyload(<Home />)
            },
            {
                name: 'createGoogleAds',
                path:'/google/google-ads',
                element:lazyload(<GoogleAd />)
            },
            {
                name: 'createGoogleAccount',
                path:'/google/account-create',
                element:lazyload(<GoogleAccountCreate />)
            },
            {
                name: 'googleAccountManagement',
                path:'/google/account-management',
                element:lazyload(<GoogleAccountManagement />)
            },
            {
                name: 'googleManagerLink',
                path:'/google/manager-link',
                element:lazyload(<GoogleManagerLink />)
            },
            {
                name: 'createFacebookAds',
                path:'/facebook/facebook-ads',
                element:lazyload(<FacebookAd />)
            },
            {
                name: 'createDemandAds',
                path:'/demand/demand-ads',
                element:lazyload(<DemandAd />)
            },
            { name: 'demandCreate', path: '/demand/create', element: lazyload(<DemandCreate />) },
            { name: 'demandRoi', path: '/demand/roi', element: lazyload(<DemandRoi />) },
            {
                name: 'createDisplayAds',
                path:'/display/display-ads',
                element:lazyload(<DisplayAd />)
            },
            {
                name: 'roi',
                path: '/roi/index',
                // * 为roi路由添加中文配置
                element: lazyload(<Roi />)
            },
            {
                name: 'gmailHourlyMetrics',
                path: '/roi/gmail-hourly-metrics',
                element: lazyload(<GmailHourlyMetrics />)
            },
            {
                name: 'adManagerDeviceSite',
                path: '/roi/admanager-device-site',
                element: lazyload(<AdManagerDeviceSite />)
            },
            {
                name: 'adManagerSiteHourly',
                path: '/roi/admanager-site-hourly',
                element: lazyload(<AdManagerSiteHourly />)
            },
            {
                name: 'channelWarning',
                path: '/roi/channel-warning',
                element: lazyload(<WarningPage />)
            },
            {
                name: 'adjustBudget',
                path: '/roi/adjust-budget',
                element: lazyload(<BudgetPage />)
            },
            {
                name: 'gAdjustment',
                path: '/roi/g-adjustment',
                element: lazyload(<GoogleAdjustment />)
            },
            {
                name: 'googlePausedCampaigns',
                path: '/roi/google-paused-campaigns',
                element: lazyload(<GooglePausedCampaigns />)
            },
            {
                name: 'fbAdjustment',
                path: '/roi/fb-adjustment',
                element: lazyload(<FacebookAdjustment />)
            },
            {
                name: 'sAdjustment',
                path: '/roi/s-adjustment',
                element: lazyload(<SnapAdjustment />)
            },
            {
                name: 'tiktokAdjustment',
                path: '/roi/tiktok-adjustment',
                element: lazyload(<TikTokAdjustment />)
            },
            {
                name: 'landingPageList',
                path: '/roi/landing-page',
                element: lazyload(<LandingPageList />)
            },
            {
                name: 'periodicData',
                path: '/roi/periodic-data',
                element: lazyload(<PeriodicDataList />)
            },
            {
                name: 'refusedAds',
                path: '/roi/refused-ads',
                element: lazyload(<RefusedAds />)
            },
            {
                name: 'copywriting',
                path: '/copywriting',
                element: lazyload(<CopyWriting />)
            },
            {
                name: 'country-store',
                path: '/country-store',
                element: lazyload(<CountryStore />)
            }
        ]
    },
    {
        name:'login',
        path: '/login',
        element: lazyload(<Login/>),
    },
    {
        name:'index',
        path: '/index',
        element: lazyload(<Index/>),
    },
    {
        name:'privacy',
        path: '/privacy',
        element: lazyload(<Privacy/>),
    },
    {
        name:'termofuse',
        path: '/termofuse',
        element: lazyload(<Terms/>),
    },
    {
        name:'redirect',
        path: '*',
        element: RedirectHome,
    }
];

export const routerConfig = createBrowserRouter(routes);

const renderRoutes = (routes: any) => {
    return (
        <>
            {routes.map((route: any) => {
                if (route.children) {
                    return <Route key={route.name} path={route.path} element={route.element}>{renderRoutes(route.children)}</Route>
                } else {
                    return <Route key={route.name} path={route.path} element={route.element} />
                }
            })}
        </>)
};

const router = () => {
    return (
        <Routes>
            {renderRoutes(routes)}
        </Routes>
    );
};

export const RouterMapping: {[key: string]: string} = {
    'display': '/display/display-ads',
    'demand': '/demand/demand-ads'
};

export default router;
